import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Save, Plus, Trash2, ArrowUp, ArrowDown, ChevronDown, ChevronUp, Play, Upload, X, AlertCircle, Search, Info } from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

const STEP_TYPES = [
  { value: "format_text",    label: "Format Text",     color: "#5C7A5E", desc: "Trim, case, remove chars" },
  { value: "format_number",  label: "Format Number",   color: "#1E40AF", desc: "Strip currency, commas" },
  { value: "format_date",    label: "Format Date",     color: "#7C3AED", desc: "Parse & reformat dates" },
  { value: "regex_extract",  label: "Regex Extract",   color: "#B45309", desc: "Extract with capture group" },
  { value: "regex_replace",  label: "Regex Replace",   color: "#92400E", desc: "Replace via regex pattern" },
  { value: "find_replace",   label: "Find & Replace",  color: "#0369A1", desc: "Literal text replacement" },
  { value: "lookup",         label: "Lookup",          color: "#5B21B6", desc: "Apply lookup table mapping" },
  { value: "cleanup",        label: "Cleanup",         color: "#9A3412", desc: "Remove noise words" },
  { value: "concat",         label: "Concatenate",     color: "#065F46", desc: "Combine multiple columns" },
  { value: "arithmetic",     label: "Arithmetic",      color: "#0F172A", desc: "+  −  ×  ÷ between columns" },
  { value: "conditional",    label: "Conditional",     color: "#831843", desc: "If/else column logic" },
  { value: "row_filter",     label: "Row Filter",      color: "#6B7280", desc: "Drop blank rows / trim bottom" },
  { value: "counter",        label: "Counter (Rank)",  color: "#047857", desc: "Dense-rank rows by a field" },
  { value: "aggregate",      label: "Aggregate (Group-By)", color: "#7C3AED", desc: "Collapse rows — sum/avg/count by group" },
];

const DATE_OUTPUT_FMTS = [
  { value: "%Y-%m-%d", label: "ISO 8601 (2024-03-15)" },
  { value: "%d/%m/%Y", label: "DD/MM/YYYY (15/03/2024)" },
  { value: "%m/%d/%Y", label: "MM/DD/YYYY (03/15/2024)" },
  { value: "%d-%m-%Y", label: "DD-MM-YYYY (15-03-2024)" },
  { value: "%Y%m%d",   label: "YYYYMMDD (20240315)" },
  { value: "%d %b %Y", label: "DD Mon YYYY (15 Mar 2024)" },
  { value: "%B %d, %Y",label: "Month DD, YYYY (March 15, 2024)" },
  { value: "%d/%m/%Y %H:%M", label: "DD/MM/YYYY HH:MM" },
  { value: "%Y-%m-%dT%H:%M:%S", label: "ISO Datetime" },
];

const DATE_INPUT_FMTS = [
  { value: "auto",        label: "Auto-detect" },
  { value: "%d/%m/%Y",    label: "DD/MM/YYYY" },
  { value: "%m/%d/%Y",    label: "MM/DD/YYYY" },
  { value: "%Y-%m-%d",    label: "YYYY-MM-DD" },
  { value: "%d-%m-%Y",    label: "DD-MM-YYYY" },
  { value: "%Y%m%d",      label: "YYYYMMDD" },
  { value: "%d %b %Y",    label: "DD Mon YYYY" },
  { value: "%d/%m/%Y %H:%M", label: "DD/MM/YYYY HH:MM" },
];

const COND_OPS = [
  { value: "eq",         label: "equals" },
  { value: "ne",         label: "not equals" },
  { value: "contains",   label: "contains" },
  { value: "starts_with",label: "starts with" },
  { value: "gt",         label: "> (numeric)" },
  { value: "lt",         label: "< (numeric)" },
  { value: "gte",        label: ">= (numeric)" },
  { value: "lte",        label: "<= (numeric)" },
  { value: "empty",      label: "is empty" },
  { value: "not_empty",  label: "is not empty" },
  { value: "regex",      label: "matches regex" },
];

const TEXT_OPS = [
  { value: "trim",               label: "Trim whitespace" },
  { value: "uppercase",          label: "UPPERCASE" },
  { value: "lowercase",          label: "lowercase" },
  { value: "title_case",         label: "Title Case" },
  { value: "remove_spaces",      label: "Remove all spaces" },
  { value: "remove_special_chars",label: "Remove special chars" },
];

function uuid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function makeStep(type) {
  const base = { id: uuid(), step_type: type, source_col: "", output_col: "", config: {} };
  if (type === "format_text") base.config = { operations: ["trim"] };
  if (type === "format_number") base.config = { strip_currency: true, strip_commas: true, strip_spaces: true };
  if (type === "format_date") base.config = { input_format: "auto", output_format: "%Y-%m-%d", dayfirst: false };
  if (type === "regex_extract") base.config = { pattern: "", group: 0, regex_id: "" };
  if (type === "regex_replace") base.config = { pattern: "", replacement: "", regex_id: "" };
  if (type === "find_replace") base.config = { find: "", replace: "", case_sensitive: false };
  if (type === "lookup") base.config = { lookup_id: "" };
  if (type === "cleanup") base.config = { cleanup_id: "" };
  if (type === "concat") { base.source_col = ""; base.config = { source_cols: ["", ""], separator: "_" }; }
  if (type === "arithmetic") base.config = { col_a: "", operator: "+", col_b: "", col_b_literal: null };
  if (type === "conditional") base.config = {
    condition_col: "", condition_op: "eq", condition_value: "",
    then_branch: { type: "literal", literal_value: "" },
    else_branch: { type: "literal", literal_value: "" },
  };
  if (type === "row_filter") base.config = { remove_blank_rows: true, trim_bottom_n: 0, blank_col_filter: "" };
  if (type === "counter") base.config = { sort_field: "", sort_desc: false };
  if (type === "aggregate") base.config = { group_by: [], agg_col: "", agg_fn: "sum", sort_field: "", sort_desc: false };
  return base;
}

// ─── Small UI primitives ──────────────────────────────────────────────────────

function Label({ children }) {
  return <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: "0.3rem" }}>{children}</label>;
}
function SI({ className = "recon-input", style = {}, ...rest }) {
  return <input className={className} style={{ width: "100%", boxSizing: "border-box", ...style }} {...rest} />;
}
function SS({ style = {}, ...rest }) {
  return <select className="recon-input" style={{ width: "100%", boxSizing: "border-box", ...style }} {...rest} />;
}
function ColSelect({ value, onChange, cols, placeholder = "— column —" }) {
  // Source column selector — dropdown when cols available, text input otherwise
  if (!cols || cols.length === 0) {
    return <SI value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} />;
  }
  return (
    <SS value={value || ""} onChange={e => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {cols.map(c => <option key={c} value={c}>{c}</option>)}
    </SS>
  );
}

// Output column — ALWAYS a text input; warns if name collides with an existing col
function OutputColInput({ value, onChange, cols = [], sourceCol = "" }) {
  const trimmed = (value || "").trim();
  const colsLower = cols.map(c => c.toLowerCase());
  const collides = trimmed && trimmed.toLowerCase() !== (sourceCol || "").toLowerCase() && colsLower.includes(trimmed.toLowerCase());
  return (
    <div>
      <SI
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder="blank = overwrite source column"
        style={{ borderColor: collides ? "#F59E0B" : undefined }}
      />
      {collides && (
        <div style={{ fontSize: "0.68rem", color: "#92400E", marginTop: "0.25rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <AlertCircle size={10} />
          Column "{trimmed}" already exists — this will <strong>overwrite</strong> it. Rename to create a separate column.
        </div>
      )}
    </div>
  );
}

// ─── Step Editors ─────────────────────────────────────────────────────────────

function FormatTextEditor({ step, cols, onChange }) {
  const cfg = step.config || {};
  const ops = cfg.operations || [];
  const toggle = (op) => {
    const next = ops.includes(op) ? ops.filter(o => o !== op) : [...ops, op];
    onChange({ ...step, config: { ...cfg, operations: next } });
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div>
        <Label>Source Column</Label>
        <ColSelect value={step.source_col} onChange={v => onChange({ ...step, source_col: v })} cols={cols} />
      </div>
      <div>
        <Label>Output Column <span style={{ fontWeight: 400, color: "var(--text-muted)", textTransform: "none", letterSpacing: 0 }}>(blank = overwrite)</span></Label>
        <OutputColInput value={step.output_col} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol={step.source_col} />
      </div>
      <div style={{ gridColumn: "1/-1" }}>
        <Label>Operations</Label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
          {TEXT_OPS.map(op => (
            <button key={op.value} onClick={() => toggle(op.value)}
              style={{ fontSize: "0.75rem", padding: "0.2rem 0.65rem", borderRadius: "9999px", border: "1px solid", cursor: "pointer",
                background: ops.includes(op.value) ? "var(--sage-light)" : "white",
                color: ops.includes(op.value) ? "var(--sage)" : "var(--text-muted)",
                borderColor: ops.includes(op.value) ? "var(--sage)" : "var(--border-default)",
                fontWeight: ops.includes(op.value) ? 600 : 400,
              }}>
              {op.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FormatNumberEditor({ step, cols, onChange }) {
  const cfg = step.config || {};
  const check = (key) => (
    <label key={key} style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.8rem", color: "var(--text-secondary)", cursor: "pointer" }}>
      <input type="checkbox" checked={!!cfg[key]} onChange={e => onChange({ ...step, config: { ...cfg, [key]: e.target.checked } })} />
      {key.replace(/_/g, " ")}
    </label>
  );
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div>
        <Label>Source Column</Label>
        <ColSelect value={step.source_col} onChange={v => onChange({ ...step, source_col: v })} cols={cols} />
      </div>
      <div>
        <Label>Output Column <span style={{ fontWeight: 400, color: "var(--text-muted)", textTransform: "none", letterSpacing: 0 }}>(blank = overwrite)</span></Label>
        <OutputColInput value={step.output_col} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol={step.source_col} />
      </div>
      <div style={{ gridColumn: "1/-1", display: "flex", gap: "1.25rem", flexWrap: "wrap" }}>
        {["strip_currency", "strip_commas", "strip_spaces"].map(check)}
      </div>
      <div>
        <Label>Round to N decimals (blank = no rounding)</Label>
        <SI type="number" min="0" max="10"
          value={cfg.round_decimals ?? ""}
          onChange={e => onChange({ ...step, config: { ...cfg, round_decimals: e.target.value !== "" ? Number(e.target.value) : null } })}
          placeholder="e.g. 2" />
      </div>
    </div>
  );
}

function FormatDateEditor({ step, cols, onChange }) {
  const cfg = step.config || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div>
        <Label>Source Column</Label>
        <ColSelect value={step.source_col} onChange={v => onChange({ ...step, source_col: v })} cols={cols} />
      </div>
      <div>
        <Label>Output Column</Label>
        <OutputColInput value={step.output_col} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol={step.source_col} />
      </div>
      <div>
        <Label>Input Format</Label>
        <SS value={cfg.input_format || "auto"} onChange={e => onChange({ ...step, config: { ...cfg, input_format: e.target.value } })}>
          {DATE_INPUT_FMTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </SS>
      </div>
      <div>
        <Label>Output Format</Label>
        <SS value={cfg.output_format || "%Y-%m-%d"} onChange={e => onChange({ ...step, config: { ...cfg, output_format: e.target.value } })}>
          {DATE_OUTPUT_FMTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </SS>
      </div>
      <div>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.8rem", color: "var(--text-secondary)", cursor: "pointer" }}>
          <input type="checkbox" checked={!!cfg.dayfirst} onChange={e => onChange({ ...step, config: { ...cfg, dayfirst: e.target.checked } })} />
          Day-first ambiguous dates (e.g. 01/02 = Jan 2 if unchecked, Feb 1 if checked)
        </label>
      </div>
    </div>
  );
}

function RegexExtractEditor({ step, cols, regexPatterns, onChange }) {
  const cfg = step.config || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div>
        <Label>Source Column</Label>
        <ColSelect value={step.source_col} onChange={v => onChange({ ...step, source_col: v })} cols={cols} />
      </div>
      <div>
        <Label>Output Column (blank = overwrite)</Label>
        <OutputColInput value={step.output_col} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol={step.source_col} />
      </div>
      <div>
        <Label>Named Pattern (or use inline below)</Label>
        <SS value={cfg.regex_id || ""} onChange={e => onChange({ ...step, config: { ...cfg, regex_id: e.target.value } })}>
          <option value="">— none (use inline pattern) —</option>
          {(regexPatterns || []).map(rp => <option key={rp.id} value={rp.id}>{rp.name}</option>)}
        </SS>
      </div>
      <div>
        <Label>Capture Group (0 = full match)</Label>
        <SI type="number" min="0" max="20"
          value={cfg.group ?? 0}
          onChange={e => onChange({ ...step, config: { ...cfg, group: Number(e.target.value) } })} />
      </div>
      {!cfg.regex_id && (
        <div style={{ gridColumn: "1/-1" }}>
          <Label>Inline Regex Pattern</Label>
          <SI value={cfg.pattern || ""} onChange={e => onChange({ ...step, config: { ...cfg, pattern: e.target.value } })}
            placeholder="e.g. BRN(\d+)" style={{ fontFamily: "JetBrains Mono, monospace" }} />
        </div>
      )}
    </div>
  );
}

// Compact help popover for regex-replace syntax. Visible only while the
// (i) icon is hovered/focused — no layout shift.
const REGEX_REPLACE_HELP = [
  { tok: "$1 $2 … $99",  note: "Nth capture group" },
  { tok: "${name}",      note: "Named group" },
  { tok: "$&",           note: "The whole match" },
  { tok: "$$",           note: "A literal $ sign" },
  { tok: "\\1 \\2 …",    note: "Python-native backrefs (still accepted)" },
];

function RegexReplaceHelp() {
  const [open, setOpen] = React.useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", marginLeft: "0.35rem" }}
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" data-testid="regex-replace-help-icon"
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}
        aria-label="Regex replacement syntax help"
        style={{ background: "none", border: "none", padding: 0, cursor: "help", color: "var(--text-muted)", display: "inline-flex" }}>
        <Info size={13} />
      </button>
      {open && (
        <div data-testid="regex-replace-help-popover"
          style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 60,
            width: "320px", background: "white", border: "1px solid var(--sage-border)",
            borderRadius: "0.5rem", padding: "0.8rem 0.9rem", boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
            fontSize: "0.75rem", color: "var(--text-primary)",
          }}>
          <div style={{ fontWeight: 600, marginBottom: "0.45rem", fontFamily: "Bricolage Grotesque, sans-serif" }}>
            Replacement syntax
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {REGEX_REPLACE_HELP.map(h => (
                <tr key={h.tok}>
                  <td style={{ padding: "0.15rem 0.5rem 0.15rem 0", fontFamily: "JetBrains Mono, monospace", color: "var(--sage)", whiteSpace: "nowrap", verticalAlign: "top" }}>{h.tok}</td>
                  <td style={{ padding: "0.15rem 0", color: "var(--text-secondary)" }}>{h.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: "0.6rem", paddingTop: "0.5rem", borderTop: "1px dashed var(--sage-border)", color: "var(--text-secondary)" }}>
            <strong style={{ color: "var(--text-primary)" }}>Example</strong>
            <div style={{ marginTop: "0.25rem", fontFamily: "JetBrains Mono, monospace" }}>
              pattern: <span style={{ color: "var(--sage)" }}>{"(\\d{3})(\\d{3})(\\d{4})"}</span><br />
              replace: <span style={{ color: "var(--sage)" }}>{"($1) $2-$3"}</span><br />
              input:   <span>2125551234</span><br />
              output:  <span style={{ color: "#065F46" }}>{"(212) 555-1234"}</span>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}

function RegexReplaceEditor({ step, cols, regexPatterns, onChange }) {
  const cfg = step.config || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div>
        <Label>Source Column</Label>
        <ColSelect value={step.source_col} onChange={v => onChange({ ...step, source_col: v })} cols={cols} />
      </div>
      <div>
        <Label>Output Column (blank = overwrite)</Label>
        <OutputColInput value={step.output_col} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol={step.source_col} />
      </div>
      <div>
        <Label>Named Pattern</Label>
        <SS value={cfg.regex_id || ""} onChange={e => onChange({ ...step, config: { ...cfg, regex_id: e.target.value } })}>
          <option value="">— inline pattern —</option>
          {(regexPatterns || []).map(rp => <option key={rp.id} value={rp.id}>{rp.name}</option>)}
        </SS>
      </div>
      <div>
        <Label>
          Replacement
          <RegexReplaceHelp />
        </Label>
        <SI data-testid="regex-replace-replacement"
          value={cfg.replacement || ""} onChange={e => onChange({ ...step, config: { ...cfg, replacement: e.target.value } })}
          placeholder="e.g. ($1) $2-$3  ·  ${name}  ·  $& for whole match  ·  $$ for literal $"
          style={{ fontFamily: "JetBrains Mono, monospace" }} />
      </div>
      {!cfg.regex_id && (
        <div style={{ gridColumn: "1/-1" }}>
          <Label>Inline Regex Pattern</Label>
          <SI value={cfg.pattern || ""} onChange={e => onChange({ ...step, config: { ...cfg, pattern: e.target.value } })}
            placeholder="e.g. (\d{3})(\d{3})(\d{4})" style={{ fontFamily: "JetBrains Mono, monospace" }} />
        </div>
      )}
    </div>
  );
}

function FindReplaceEditor({ step, cols, onChange }) {
  const cfg = step.config || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div>
        <Label>Source Column</Label>
        <ColSelect value={step.source_col} onChange={v => onChange({ ...step, source_col: v })} cols={cols} />
      </div>
      <div>
        <Label>Output Column (blank = overwrite)</Label>
        <OutputColInput value={step.output_col} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol={step.source_col} />
      </div>
      <div>
        <Label>Find</Label>
        <SI value={cfg.find || ""} onChange={e => onChange({ ...step, config: { ...cfg, find: e.target.value } })} placeholder="Text to find" />
      </div>
      <div>
        <Label>Replace with</Label>
        <SI value={cfg.replace || ""} onChange={e => onChange({ ...step, config: { ...cfg, replace: e.target.value } })} placeholder="Replacement (blank = delete)" />
      </div>
      <div>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.8rem", color: "var(--text-secondary)", cursor: "pointer" }}>
          <input type="checkbox" checked={!!cfg.case_sensitive} onChange={e => onChange({ ...step, config: { ...cfg, case_sensitive: e.target.checked } })} />
          Case sensitive
        </label>
      </div>
    </div>
  );
}

function LookupEditor({ step, cols, lookupTables, onChange }) {
  const cfg = step.config || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div>
        <Label>Source Column</Label>
        <ColSelect value={step.source_col} onChange={v => onChange({ ...step, source_col: v })} cols={cols} />
      </div>
      <div>
        <Label>Output Column (blank = overwrite)</Label>
        <OutputColInput value={step.output_col} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol={step.source_col} />
      </div>
      <div style={{ gridColumn: "1/-1" }}>
        <Label>Lookup Table</Label>
        <SS value={cfg.lookup_id || ""} onChange={e => onChange({ ...step, config: { ...cfg, lookup_id: e.target.value } })}>
          <option value="">— select lookup table —</option>
          {(lookupTables || []).map(lt => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
        </SS>
      </div>
    </div>
  );
}

function CleanupEditor({ step, cols, cleanupTables, onChange }) {
  const cfg = step.config || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div>
        <Label>Source Column</Label>
        <ColSelect value={step.source_col} onChange={v => onChange({ ...step, source_col: v })} cols={cols} />
      </div>
      <div>
        <Label>Output Column (blank = overwrite)</Label>
        <OutputColInput value={step.output_col} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol={step.source_col} />
      </div>
      <div style={{ gridColumn: "1/-1" }}>
        <Label>Cleanup Table</Label>
        <SS value={cfg.cleanup_id || ""} onChange={e => onChange({ ...step, config: { ...cfg, cleanup_id: e.target.value } })}>
          <option value="">— select cleanup table —</option>
          {(cleanupTables || []).map(ct => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
        </SS>
      </div>
    </div>
  );
}

function ConcatEditor({ step, cols, onChange }) {
  const cfg = step.config || {};
  const srcCols = cfg.source_cols || ["", ""];
  const setCols = (arr) => onChange({ ...step, config: { ...cfg, source_cols: arr } });
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div style={{ gridColumn: "1/-1" }}>
        <Label>Columns to concatenate (in order)</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          {srcCols.map((c, i) => (
            <div key={i} style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
              <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", width: "1.2rem", flexShrink: 0, textAlign: "right" }}>{i + 1}</span>
              <ColSelect value={c} onChange={v => { const n = [...srcCols]; n[i] = v; setCols(n); }} cols={cols} />
              {srcCols.length > 2 && (
                <button onClick={() => setCols(srcCols.filter((_, j) => j !== i))}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}>
                  <X size={13} />
                </button>
              )}
            </div>
          ))}
          <button className="btn-secondary" onClick={() => setCols([...srcCols, ""])}
            style={{ alignSelf: "flex-start", fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}>
            <Plus size={11} style={{ marginRight: 4 }} /> Add column
          </button>
        </div>
      </div>
      <div>
        <Label>Separator</Label>
        <SI value={cfg.separator || ""} onChange={e => onChange({ ...step, config: { ...cfg, separator: e.target.value } })}
          placeholder="e.g. _ or blank for none" />
      </div>
      <div>
        <Label>Output Column (required)</Label>
        <OutputColInput value={step.output_col || ""} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol="" />
      </div>
    </div>
  );
}

function ArithmeticEditor({ step, cols, onChange }) {
  const cfg = step.config || {};
  const isLiteral = cfg.col_b_literal !== null && cfg.col_b_literal !== undefined && cfg.col_b === "";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "0.75rem", alignItems: "end" }}>
      <div>
        <Label>Column A</Label>
        <ColSelect value={cfg.col_a || ""} onChange={v => onChange({ ...step, config: { ...cfg, col_a: v } })} cols={cols} />
      </div>
      <div>
        <Label>Operator</Label>
        <SS value={cfg.operator || "+"} onChange={e => onChange({ ...step, config: { ...cfg, operator: e.target.value } })} style={{ width: "60px" }}>
          {["+", "-", "*", "/"].map(op => <option key={op} value={op}>{op}</option>)}
        </SS>
      </div>
      <div>
        <Label>Column B or Literal value</Label>
        <div style={{ display: "flex", gap: "0.3rem" }}>
          <ColSelect value={isLiteral ? "" : (cfg.col_b || "")} onChange={v => onChange({ ...step, config: { ...cfg, col_b: v, col_b_literal: null } })} cols={cols} placeholder="— column —" />
          <span style={{ lineHeight: "2rem", color: "var(--text-muted)", flexShrink: 0 }}>or</span>
          <SI type="number" value={isLiteral ? cfg.col_b_literal : ""} placeholder="literal"
            style={{ width: "80px" }}
            onChange={e => onChange({ ...step, config: { ...cfg, col_b: "", col_b_literal: e.target.value !== "" ? Number(e.target.value) : null } })} />
        </div>
      </div>
      <div style={{ gridColumn: "1/-1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <div />
        <div>
          <Label>Output Column (required)</Label>
          <OutputColInput value={step.output_col || ""} onChange={v => onChange({ ...step, output_col: v })} cols={cols} sourceCol={cfg.col_a || ""} />
        </div>
      </div>
    </div>
  );
}

// ── Conditional: branch types ───────────────────────────────────────────────

const BRANCH_TYPES = [
  { value: "literal",       label: "Literal value" },
  { value: "column",        label: "From column" },
  { value: "regex_extract", label: "Regex extract" },
  { value: "conditional",   label: "Nested conditional" },
];

function _emptyBranch(type = "literal") {
  if (type === "literal")       return { type: "literal", literal_value: "" };
  if (type === "column")        return { type: "column", from_column: "" };
  if (type === "regex_extract") return { type: "regex_extract", source_column: "", regex_id: "", pattern: "", group: 0, fallback: "" };
  if (type === "conditional")   return {
    type: "conditional",
    nested_config: {
      condition_col: "", condition_op: "eq", condition_value: "",
      then_branch: { type: "literal", literal_value: "" },
      else_branch: { type: "literal", literal_value: "" },
    }
  };
  return { type: "literal", literal_value: "" };
}

// Migrate a legacy config (flat then_col/then_literal/else_col/else_literal)
// to the new branch shape — non-destructive, only runs when branches are missing.
function _ensureBranches(cfg) {
  if (!cfg) return cfg;
  const out = { ...cfg };
  if (!out.then_branch) {
    out.then_branch = out.then_col
      ? { type: "column", from_column: out.then_col }
      : { type: "literal", literal_value: out.then_literal || "" };
  }
  if (!out.else_branch) {
    out.else_branch = out.else_col
      ? { type: "column", from_column: out.else_col }
      : { type: "literal", literal_value: out.else_literal || "" };
  }
  return out;
}

function BranchEditor({ branch, onChange, cols, regexPatterns, depth, side, ConditionalEditorRef }) {
  const b = branch || _emptyBranch("literal");
  const setType = (newType) => onChange(_emptyBranch(newType));
  const allowNested = depth < 2;  // outer (0) → branch (1) → branch (2) is max

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      <div>
        <Label>Branch type</Label>
        <SS data-testid={`cond-branch-type-${side}-${depth}`} value={b.type || "literal"} onChange={e => setType(e.target.value)}>
          {BRANCH_TYPES.map(t => (
            <option key={t.value} value={t.value} disabled={t.value === "conditional" && !allowNested}>
              {t.label}{t.value === "conditional" && !allowNested ? " (max depth)" : ""}
            </option>
          ))}
        </SS>
      </div>

      {b.type === "literal" && (
        <div>
          <Label>Literal value</Label>
          <SI value={b.literal_value || ""} onChange={e => onChange({ ...b, literal_value: e.target.value })} placeholder="Literal text" />
        </div>
      )}

      {b.type === "column" && (
        <div>
          <Label>Copy from column</Label>
          <ColSelect value={b.from_column || ""} onChange={v => onChange({ ...b, from_column: v })} cols={cols} placeholder="— pick column —" />
        </div>
      )}

      {b.type === "regex_extract" && (
        <>
          <div>
            <Label>Source column</Label>
            <ColSelect value={b.source_column || ""} onChange={v => onChange({ ...b, source_column: v })} cols={cols} placeholder="— column to search —" />
          </div>
          <div>
            <Label>Saved regex pattern (optional)</Label>
            <SS value={b.regex_id || ""} onChange={e => onChange({ ...b, regex_id: e.target.value })}>
              <option value="">— or type inline below —</option>
              {(regexPatterns || []).map(rp => (
                <option key={rp.id} value={rp.id}>{rp.name}{rp.pattern ? `  ·  ${String(rp.pattern).slice(0, 40)}` : ""}</option>
              ))}
            </SS>
          </div>
          {!b.regex_id && (
            <div>
              <Label>Inline regex pattern</Label>
              <SI value={b.pattern || ""} onChange={e => onChange({ ...b, pattern: e.target.value })}
                placeholder="e.g. FT(\\d+) — use () for capture groups"
                style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.78rem" }} />
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "0.5rem" }}>
            <div>
              <Label>Capture group #</Label>
              <SI type="number" min="0" max="9" value={b.group ?? 0}
                onChange={e => onChange({ ...b, group: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Fallback if no match</Label>
              <SI value={b.fallback || ""} onChange={e => onChange({ ...b, fallback: e.target.value })} placeholder="(empty by default)" />
            </div>
          </div>
        </>
      )}

      {b.type === "conditional" && ConditionalEditorRef && (
        <div style={{ padding: "0.5rem", background: "white", borderRadius: "0.35rem", border: "1px dashed var(--border-default)" }}>
          <ConditionalEditorRef
            step={{ config: b.nested_config }}
            cols={cols}
            regexPatterns={regexPatterns}
            onChange={next => onChange({ ...b, nested_config: next.config })}
            nestedDepth={depth + 1}
            hideOutputCol
          />
        </div>
      )}
    </div>
  );
}

function ConditionalEditor({ step, cols, regexPatterns, onChange, nestedDepth = 0, hideOutputCol = false }) {
  const cfg = _ensureBranches(step.config || {});
  const noValueOps = ["empty", "not_empty"];
  const setCfg = (updater) => onChange({ ...step, config: typeof updater === "function" ? updater(cfg) : updater });

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div style={{ gridColumn: "1/-1", padding: "0.75rem", background: "var(--bg-subtle)", borderRadius: "0.5rem", border: "1px dashed var(--border-default)" }}>
        <p style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 0.5rem" }}>
          Condition{nestedDepth > 0 ? ` · nested level ${nestedDepth}` : ""}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
          <div>
            <Label>Column</Label>
            <ColSelect value={cfg.condition_col || ""} onChange={v => setCfg({ ...cfg, condition_col: v })} cols={cols} />
          </div>
          <div>
            <Label>Operator</Label>
            <SS value={cfg.condition_op || "eq"} onChange={e => setCfg({ ...cfg, condition_op: e.target.value })}>
              {COND_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </SS>
          </div>
          {!noValueOps.includes(cfg.condition_op) && (
            <div>
              <Label>Value</Label>
              <SI value={cfg.condition_value || ""} onChange={e => setCfg({ ...cfg, condition_value: e.target.value })}
                placeholder="Compare value" />
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: "0.75rem", background: "#F0FDF4", borderRadius: "0.5rem", border: "1px dashed #86EFAC" }}>
        <p style={{ fontSize: "0.72rem", fontWeight: 600, color: "#166534", textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 0.5rem" }}>If TRUE → output</p>
        <BranchEditor branch={cfg.then_branch} onChange={next => setCfg({ ...cfg, then_branch: next })}
          cols={cols} regexPatterns={regexPatterns} depth={nestedDepth} side="true" ConditionalEditorRef={ConditionalEditor} />
      </div>
      <div style={{ padding: "0.75rem", background: "#FEF2F2", borderRadius: "0.5rem", border: "1px dashed #FECACA" }}>
        <p style={{ fontSize: "0.72rem", fontWeight: 600, color: "#991B1B", textTransform: "uppercase", letterSpacing: "0.04em", margin: "0 0 0.5rem" }}>If FALSE → output</p>
        <BranchEditor branch={cfg.else_branch} onChange={next => setCfg({ ...cfg, else_branch: next })}
          cols={cols} regexPatterns={regexPatterns} depth={nestedDepth} side="false" ConditionalEditorRef={ConditionalEditor} />
      </div>
      {!hideOutputCol && (
        <div style={{ gridColumn: "1/-1" }}>
          <Label>Output Column (required)</Label>
          <SI value={step.output_col || ""} onChange={e => onChange({ ...step, output_col: e.target.value })}
            placeholder="New column name" />
        </div>
      )}
    </div>
  );
}

function RowFilterEditor({ step, cols, onChange }) {
  const cfg = step.config || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div style={{ gridColumn: "1/-1" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem", color: "var(--text-secondary)", cursor: "pointer" }}>
          <input type="checkbox" checked={!!cfg.remove_blank_rows}
            onChange={e => onChange({ ...step, config: { ...cfg, remove_blank_rows: e.target.checked } })} />
          Remove rows where ALL columns are blank
        </label>
      </div>
      <div>
        <Label>Remove bottom N rows (0 = none)</Label>
        <SI type="number" min="0" max="1000"
          value={cfg.trim_bottom_n ?? 0}
          onChange={e => onChange({ ...step, config: { ...cfg, trim_bottom_n: Number(e.target.value) } })} />
      </div>
      <div>
        <Label>Drop rows where this column is blank</Label>
        <ColSelect value={cfg.blank_col_filter || ""} onChange={v => onChange({ ...step, config: { ...cfg, blank_col_filter: v } })} cols={cols} placeholder="— optional —" />
      </div>
    </div>
  );
}

function CounterEditor({ step, cols, onChange }) {
  const cfg = step.config || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div>
        <Label>Partition Field (required)</Label>
        <ColSelect
          value={step.source_col || ""}
          onChange={v => onChange({ ...step, source_col: v })}
          cols={cols}
          placeholder="— Column whose values define groups —"
        />
        <p style={{ fontSize: "0.68rem", color: "var(--text-muted)", margin: "0.25rem 0 0" }}>
          Each distinct value gets its own 1..N rank sequence.
        </p>
      </div>
      <div>
        <Label>Sort Field (optional)</Label>
        <ColSelect
          value={cfg.sort_field || ""}
          onChange={v => onChange({ ...step, config: { ...cfg, sort_field: v } })}
          cols={cols}
          placeholder="— preserve input order —"
        />
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.72rem", color: "var(--text-secondary)", marginTop: "0.4rem", cursor: "pointer" }}>
          <input type="checkbox" checked={!!cfg.sort_desc}
            onChange={e => onChange({ ...step, config: { ...cfg, sort_desc: e.target.checked } })} />
          Sort descending
        </label>
      </div>
      <div style={{ gridColumn: "1/-1" }}>
        <Label>Output Column (required)</Label>
        <SI
          data-testid="counter-output-col"
          value={step.output_col || ""}
          onChange={e => onChange({ ...step, output_col: e.target.value })}
          placeholder={step.source_col ? `${step.source_col}_rank` : "e.g. occurrence_no"}
        />
        <p style={{ fontSize: "0.68rem", color: "var(--text-muted)", margin: "0.25rem 0 0" }}>
          Writes a sequential number (1, 2, 3…) per row within its partition.
        </p>
      </div>
    </div>
  );
}

const AGG_FNS = [
  { value: "sum",   label: "Sum"            },
  { value: "avg",   label: "Average"        },
  { value: "count", label: "Count (rows)"   },
  { value: "min",   label: "Min"            },
  { value: "max",   label: "Max"            },
  { value: "first", label: "First value"    },
  { value: "last",  label: "Last value"     },
];

function AggregateEditor({ step, cols, onChange }) {
  const cfg = step.config || {};
  const groupBy = cfg.group_by && cfg.group_by.length ? cfg.group_by : [""];
  const setGroupBy = (arr) => onChange({ ...step, config: { ...cfg, group_by: arr } });
  const needsAggCol = cfg.agg_fn !== "count";

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
      <div style={{ gridColumn: "1/-1" }}>
        <Label>Group By Columns (required) — all rows sharing these values collapse into one</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
          {groupBy.map((c, i) => (
            <div key={i} style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
              <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", width: "1.2rem", flexShrink: 0, textAlign: "right" }}>{i + 1}</span>
              <ColSelect value={c} onChange={v => { const n = [...groupBy]; n[i] = v; setGroupBy(n); }} cols={cols} placeholder="— pick column —" />
              {groupBy.length > 1 && (
                <button onClick={() => setGroupBy(groupBy.filter((_, j) => j !== i))}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}>
                  <X size={13} />
                </button>
              )}
            </div>
          ))}
          <button className="btn-secondary" onClick={() => setGroupBy([...groupBy, ""])}
            style={{ alignSelf: "flex-start", fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}>
            <Plus size={11} style={{ marginRight: 4 }} /> Add column
          </button>
        </div>
      </div>
      <div>
        <Label>Aggregate Function</Label>
        <select
          data-testid="agg-fn-select"
          value={cfg.agg_fn || "sum"}
          onChange={e => onChange({ ...step, config: { ...cfg, agg_fn: e.target.value } })}
          style={{ width: "100%", padding: "0.4rem 0.5rem", fontSize: "0.8rem", border: "1px solid var(--border-default)", borderRadius: "0.35rem", background: "white" }}
        >
          {AGG_FNS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>
      <div>
        <Label>Aggregate Column {needsAggCol ? "(required)" : "(not used for Count)"}</Label>
        <ColSelect
          value={cfg.agg_col || ""}
          onChange={v => onChange({ ...step, config: { ...cfg, agg_col: v } })}
          cols={cols}
          placeholder={needsAggCol ? "— column to aggregate —" : "—"}
        />
      </div>
      <div>
        <Label>Sort Field (optional)</Label>
        <ColSelect
          value={cfg.sort_field || ""}
          onChange={v => onChange({ ...step, config: { ...cfg, sort_field: v } })}
          cols={cols}
          placeholder="— retain values from first row —"
        />
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.72rem", color: "var(--text-secondary)", marginTop: "0.4rem", cursor: "pointer" }}>
          <input type="checkbox" checked={!!cfg.sort_desc}
            onChange={e => onChange({ ...step, config: { ...cfg, sort_desc: e.target.checked } })} />
          Sort descending (retain values from the <em>last</em> row instead of the first)
        </label>
      </div>
      <div>
        <Label>Output Column (defaults to Aggregate Column)</Label>
        <SI
          data-testid="agg-output-col"
          value={step.output_col || ""}
          onChange={e => onChange({ ...step, output_col: e.target.value })}
          placeholder={cfg.agg_col || "e.g. total_amount"}
        />
      </div>
      <div style={{ gridColumn: "1/-1", padding: "0.5rem 0.75rem", background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: "0.35rem", fontSize: "0.7rem", color: "#5B21B6" }}>
        <strong>Note:</strong> This step <em>reduces the dataset size</em> — one row per unique combination of Group-By values. All non-aggregate columns keep the value from the sorted first (or last if descending) row of each group.
      </div>
    </div>
  );
}

function StepEditor({ step, index, total, cols, lookupTables, cleanupTables, regexPatterns, onChange, onDelete, onMove }) {
  const [expanded, setExpanded] = useState(true);
  const typeInfo = STEP_TYPES.find(t => t.value === step.step_type) || {};
  const color = typeInfo.color || "#666";
  const enabled = step.enabled !== false;
  const toggleEnabled = (e) => { e.stopPropagation(); onChange({ ...step, enabled: !enabled }); };

  const summary = () => {
    const src = step.source_col || (step.config?.source_cols?.filter(Boolean)?.join("+")) || "";
    const out = step.output_col || src || "?";
    if (src && src !== out) return `${src} → ${out}`;
    return out || "—";
  };

  return (
    <div data-testid={`step-block-${index}`} style={{ borderRadius: "0.5rem", border: "1px solid var(--border-default)", background: "white", marginBottom: "0.5rem", opacity: enabled ? 1 : 0.6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.65rem 0.75rem", cursor: "pointer", background: enabled ? "transparent" : "repeating-linear-gradient(135deg, transparent 0 6px, rgba(0,0,0,0.025) 6px 12px)" }} onClick={() => setExpanded(!expanded)}>
        <span style={{ fontSize: "0.62rem", padding: "0.15rem 0.5rem", borderRadius: "9999px", background: `${color}18`, color, border: `1px solid ${color}40`, fontWeight: 600, flexShrink: 0 }}>
          {typeInfo.label || step.step_type}
        </span>
        <span style={{ fontSize: "0.8rem", fontFamily: "JetBrains Mono, monospace", color: "var(--text-secondary)", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: enabled ? "none" : "line-through" }}>
          {summary()}
        </span>
        {!enabled && (
          <span data-testid={`step-disabled-pill-${index}`} style={{ fontSize: "0.6rem", padding: "0.1rem 0.45rem", borderRadius: "9999px", background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Disabled
          </span>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          <button
            data-testid={`step-toggle-${index}`}
            onClick={toggleEnabled}
            title={enabled ? "Disable step (skipped in runs & previews)" : "Enable step"}
            style={{
              position: "relative",
              width: "1.85rem", height: "1rem",
              background: enabled ? color : "#D1D5DB",
              border: "none", borderRadius: "9999px",
              cursor: "pointer", padding: 0, flexShrink: 0,
              transition: "background 0.15s ease",
              marginRight: "0.1rem",
            }}>
            <span style={{
              position: "absolute", top: "2px", left: enabled ? "calc(100% - 0.8rem - 2px)" : "2px",
              width: "0.8rem", height: "0.8rem",
              background: "white", borderRadius: "9999px",
              boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
              transition: "left 0.15s ease",
            }} />
          </button>
          <button onClick={e => { e.stopPropagation(); onMove(index, -1); }} disabled={index === 0}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", opacity: index === 0 ? 0.3 : 1, padding: "0.15rem" }}>
            <ArrowUp size={12} />
          </button>
          <button onClick={e => { e.stopPropagation(); onMove(index, 1); }} disabled={index === total - 1}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", opacity: index === total - 1 ? 0.3 : 1, padding: "0.15rem" }}>
            <ArrowDown size={12} />
          </button>
          <button onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: "0.15rem" }}>
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          <button onClick={e => { e.stopPropagation(); onDelete(index); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", padding: "0.15rem" }}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      {expanded && (
        <div style={{ padding: "0 0.75rem 0.75rem" }}>
          <div style={{ borderTop: "1px solid var(--border-default)", paddingTop: "0.75rem" }}>
            {step.step_type === "format_text"   && <FormatTextEditor   step={step} cols={cols} onChange={onChange} />}
            {step.step_type === "format_number"  && <FormatNumberEditor  step={step} cols={cols} onChange={onChange} />}
            {step.step_type === "format_date"    && <FormatDateEditor    step={step} cols={cols} onChange={onChange} />}
            {step.step_type === "regex_extract"  && <RegexExtractEditor  step={step} cols={cols} regexPatterns={regexPatterns} onChange={onChange} />}
            {step.step_type === "regex_replace"  && <RegexReplaceEditor  step={step} cols={cols} regexPatterns={regexPatterns} onChange={onChange} />}
            {step.step_type === "find_replace"   && <FindReplaceEditor   step={step} cols={cols} onChange={onChange} />}
            {step.step_type === "lookup"         && <LookupEditor        step={step} cols={cols} lookupTables={lookupTables} onChange={onChange} />}
            {step.step_type === "cleanup"        && <CleanupEditor       step={step} cols={cols} cleanupTables={cleanupTables} onChange={onChange} />}
            {step.step_type === "concat"         && <ConcatEditor        step={step} cols={cols} onChange={onChange} />}
            {step.step_type === "arithmetic"     && <ArithmeticEditor    step={step} cols={cols} onChange={onChange} />}
            {step.step_type === "conditional"    && <ConditionalEditor   step={step} cols={cols} regexPatterns={regexPatterns} onChange={onChange} />}
            {step.step_type === "row_filter"      && <RowFilterEditor     step={step} cols={cols} onChange={onChange} />}
            {step.step_type === "counter"         && <CounterEditor       step={step} cols={cols} onChange={onChange} />}
            {step.step_type === "aggregate"       && <AggregateEditor     step={step} cols={cols} onChange={onChange} />}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Preview Panel ────────────────────────────────────────────────────────────

const ROW_LIMITS = [5, 10, 25, 50, 100, null];
const ROW_LIMIT_LABELS = { 5: "5", 10: "10", 25: "25", 50: "50", 100: "100", null: "All" };

function parseCSV(text) {
  const lines = text.trim().split("\n").filter(Boolean);
  if (!lines.length) return [];
  const header = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
  return lines.slice(1).map(line => {
    const vals = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
    const row = {};
    header.forEach((h, i) => { row[h] = vals[i] ?? ""; });
    return row;
  });
}

function PreviewPanel({ steps, onColumnsLoaded }) {
  const [source, setSource] = useState("paste");
  const [csvText, setCsvText] = useState("");
  const [fileId, setFileId] = useState(null);
  const [fileSheets, setFileSheets] = useState([]);
  const [fileSheet, setFileSheet] = useState(null);
  const [fileSkip, setFileSkip] = useState(0);
  const [rowLimit, setRowLimit] = useState(25);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState("");
  const [colFilterField, setColFilterField] = useState("");
  const [colFilterOp, setColFilterOp] = useState("contains");
  const [colFilterValue, setColFilterValue] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const fileRef = useRef(null);

  const runPreview = useCallback(async () => {
    setErr(""); setRunning(true);
    try {
      const payload = { steps, row_limit: rowLimit };
      if (source === "paste") {
        const rows = parseCSV(csvText);
        if (!rows.length) { setErr("No data to preview. Paste CSV rows first."); setRunning(false); return; }
        payload.rows = rows;
      } else {
        if (!fileId) { setErr("Upload a file first."); setRunning(false); return; }
        payload.file_id = fileId;
        payload.sheet_name = fileSheet || null;
        payload.skip_rows = Number(fileSkip || 0);
      }
      const r = await api.post("/transform-sets/preview", payload);
      setResult(r.data);
      if (onColumnsLoaded && r.data.final_columns) {
        onColumnsLoaded(r.data.final_columns);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Preview failed");
    } finally {
      setRunning(false);
    }
  }, [steps, source, csvText, fileId, fileSheet, fileSkip, rowLimit, onColumnsLoaded]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/reconciliation/upload-file", fd);
      setFileId(r.data.file_id);
      setFileSheets(r.data.sheets || []);
      setFileSheet(r.data.sheets?.[0] || null);
    } catch (ex) {
      setErr("Upload failed: " + (ex.response?.data?.detail || ex.message));
    }
  };

  // Step-by-step summary: count rows changed at each step
  const stepSummary = result?.rows?.length > 0
    ? result.rows[0].step_results?.map((_, si) => {
      const changed = result.rows.filter(r => Object.keys(r.step_results[si]?.changed || {}).length > 0 || Object.keys(r.step_results[si]?.added || {}).length > 0).length;
      return changed;
    })
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: "0.75rem" }}>
      {/* Source tabs */}
      <div style={{ display: "flex", gap: "0.5rem" }}>
        {["paste", "file"].map(s => (
          <button key={s} onClick={() => setSource(s)}
            style={{ fontSize: "0.78rem", padding: "0.3rem 0.75rem", borderRadius: "9999px", border: "1px solid", cursor: "pointer",
              background: source === s ? "var(--sage)" : "white",
              color: source === s ? "white" : "var(--text-muted)",
              borderColor: source === s ? "var(--sage)" : "var(--border-default)", fontWeight: source === s ? 600 : 400 }}>
            {s === "paste" ? "Paste CSV" : "Upload File"}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>Rows:</span>
          {ROW_LIMITS.map(l => (
            <button key={String(l)} onClick={() => setRowLimit(l)}
              style={{ fontSize: "0.68rem", padding: "0.15rem 0.45rem", borderRadius: "9999px", border: "1px solid", cursor: "pointer",
                background: rowLimit === l ? "var(--sage)" : "white", color: rowLimit === l ? "white" : "var(--text-muted)",
                borderColor: rowLimit === l ? "var(--sage)" : "var(--border-default)" }}>
              {ROW_LIMIT_LABELS[l]}
            </button>
          ))}
        </div>
      </div>

      {/* Input area */}
      {source === "paste" ? (
        <textarea
          data-testid="preview-csv-paste"
          value={csvText}
          onChange={e => setCsvText(e.target.value)}
          placeholder={"Paste CSV here. First line = headers.\n\nExample:\nNAME,AMOUNT,DATE\nJohn Doe,1234.56,15/03/2024\nJane Smith,$2,000.00,01/01/2024"}
          style={{ width: "100%", boxSizing: "border-box", height: "120px", resize: "vertical", fontSize: "0.78rem", fontFamily: "JetBrains Mono, monospace", padding: "0.625rem", borderRadius: "0.375rem", border: "1px solid var(--border-default)", color: "var(--text-primary)", background: "var(--bg-default)" }}
        />
      ) : (
        <div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={handleUpload} />
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
            <button className="btn-secondary" onClick={() => fileRef.current?.click()} style={{ fontSize: "0.78rem", padding: "0.35rem 0.75rem", display: "flex", gap: "0.35rem", alignItems: "center" }}>
              <Upload size={13} /> {fileId ? "Change file" : "Upload sample file"}
            </button>
            {fileId && fileSheets.length > 0 && (
              <select className="recon-input" style={{ fontSize: "0.78rem", padding: "0.3rem 0.5rem", width: "auto" }}
                value={fileSheet || ""} onChange={e => setFileSheet(e.target.value)}>
                {fileSheets.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            {fileId && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
                <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>Skip top rows:</span>
                <input type="number" min="0" max="50" className="recon-input"
                  style={{ width: "56px", fontSize: "0.78rem", padding: "0.3rem 0.4rem" }}
                  value={fileSkip} onChange={e => setFileSkip(Number(e.target.value))} />
              </div>
            )}
          </div>
        </div>
      )}

      {err && (
        <div style={{ padding: "0.5rem 0.75rem", background: "#FEF2F2", borderRadius: "0.375rem", fontSize: "0.78rem", color: "#991B1B", display: "flex", gap: "0.4rem", alignItems: "center" }}>
          <AlertCircle size={13} /> {err}
        </div>
      )}

      <button data-testid="run-preview-btn" className="btn-primary" onClick={runPreview} disabled={running}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", fontSize: "0.82rem", padding: "0.5rem 1rem" }}>
        {running ? "Running…" : <><Play size={13} /> Run Preview</>}
      </button>

      {/* Results */}
      {result && (
        <div style={{ flex: 1, overflow: "auto" }}>
          {/* Step summary bar */}
          {stepSummary && steps.length > 0 && (
            <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
              {steps.map((s, i) => {
                const typeInfo = STEP_TYPES.find(t => t.value === s.step_type);
                const color = typeInfo?.color || "#666";
                const changed = stepSummary[i] || 0;
                return (
                  <div key={s.id} style={{ fontSize: "0.68rem", padding: "0.15rem 0.5rem", borderRadius: "9999px",
                    background: changed > 0 ? `${color}15` : "#F3F4F6",
                    border: `1px solid ${changed > 0 ? color + "40" : "#E5E7EB"}`,
                    color: changed > 0 ? color : "var(--text-muted)" }}>
                    {i + 1}. {typeInfo?.label || s.step_type} · {changed} rows
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary badges */}
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
            {result.added_columns.length > 0 && (
              <span style={{ fontSize: "0.72rem", padding: "0.15rem 0.5rem", borderRadius: "9999px", background: "#D1FAE5", color: "#065F46", border: "1px solid #6EE7B7" }}>
                {result.added_columns.length} new col{result.added_columns.length !== 1 ? "s" : ""}: {result.added_columns.join(", ")}
              </span>
            )}
            {result.modified_columns.length > 0 && (
              <span style={{ fontSize: "0.72rem", padding: "0.15rem 0.5rem", borderRadius: "9999px", background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" }}>
                {result.modified_columns.length} modified: {result.modified_columns.join(", ")}
              </span>
            )}
            <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{result.rows.length} row{result.rows.length !== 1 ? "s" : ""} previewed</span>
          </div>

          {/* Column filter bar */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
            <Search size={11} style={{ color: "var(--text-muted)" }} />
            <select data-testid="preview-filter-field" value={colFilterField} onChange={e => { setColFilterField(e.target.value); setColFilterValue(""); }}
              style={{ fontSize: "0.75rem", padding: "0.2rem 0.35rem", border: "1px solid var(--border-default)", borderRadius: "0.25rem", background: "var(--bg-subtle)", color: colFilterField ? "var(--text-primary)" : "var(--text-muted)" }}>
              <option value="">Filter column…</option>
              {result.final_columns.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {colFilterField && (
              <>
                <select value={colFilterOp} onChange={e => setColFilterOp(e.target.value)}
                  data-testid="preview-filter-op"
                  style={{ fontSize: "0.75rem", padding: "0.2rem 0.35rem", border: "1px solid var(--border-default)", borderRadius: "0.25rem", background: "var(--bg-subtle)" }}>
                  <option value="contains">contains</option>
                  <option value="equals">equals</option>
                  <option value="starts_with">starts with</option>
                  <option value="not_equals">≠</option>
                </select>
                <input data-testid="preview-filter-value" value={colFilterValue} onChange={e => setColFilterValue(e.target.value)}
                  placeholder="value…"
                  style={{ fontSize: "0.75rem", padding: "0.2rem 0.45rem", border: "1px solid var(--border-default)", borderRadius: "0.25rem", minWidth: "80px" }} />
              </>
            )}
            {(colFilterField || colFilterValue) && (
              <button onClick={() => { setColFilterField(""); setColFilterValue(""); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", padding: 0 }}>
                <X size={12} />
              </button>
            )}
            <span style={{ marginLeft: "auto", fontSize: "0.7rem", color: "var(--text-muted)" }}>Click row to expand</span>
          </div>

          {/* Table */}
          {result.rows.length > 0 && (() => {
            const filteredRows = result.rows.filter(row => {
              if (!colFilterField || !colFilterValue.trim()) return true;
              const cell = String(row.final?.[colFilterField] ?? "").toLowerCase();
              const v = colFilterValue.toLowerCase();
              if (colFilterOp === "contains")    return cell.includes(v);
              if (colFilterOp === "equals")      return cell === v;
              if (colFilterOp === "starts_with") return cell.startsWith(v);
              if (colFilterOp === "not_equals")  return cell !== v;
              return true;
            });
            return (
              <div style={{ overflowX: "auto", border: "1px solid var(--border-default)", borderRadius: "0.5rem" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.72rem" }}>
                  <thead>
                    <tr>
                      {result.final_columns.map(col => (
                        <th key={col} style={{
                          padding: "0.4rem 0.6rem", textAlign: "left", borderBottom: "1px solid var(--border-default)",
                          background: result.added_columns.includes(col) ? "#D1FAE5" : result.modified_columns.includes(col) ? "#FEF3C7" : "var(--bg-subtle)",
                          fontWeight: 600, color: result.added_columns.includes(col) ? "#065F46" : result.modified_columns.includes(col) ? "#92400E" : "var(--text-secondary)",
                          whiteSpace: "nowrap",
                        }}>
                          {col}
                          {result.added_columns.includes(col) && <span style={{ marginLeft: 4, fontSize: "0.6rem", background: "#065F46", color: "white", padding: "0.05rem 0.3rem", borderRadius: "9999px" }}>NEW</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 ? (
                      <tr><td colSpan={result.final_columns.length} style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)", fontSize: "0.8rem" }}>No rows match filter</td></tr>
                    ) : filteredRows.map((row, ri) => (
                      <tr key={ri} data-testid={`preview-row-${ri}`} onClick={() => setExpandedRow(row)}
                        style={{ borderBottom: "1px solid var(--border-default)", cursor: "pointer" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                        onMouseLeave={e => e.currentTarget.style.background = "white"}>
                        {result.final_columns.map(col => {
                          const orig = row.original?.[col];
                          const final = row.final?.[col];
                          const isNew = result.added_columns.includes(col);
                          const isModified = !isNew && orig !== undefined && orig !== final;
                          return (
                            <td key={col} style={{
                              padding: "0.35rem 0.6rem",
                              background: isNew ? "#F0FDF4" : isModified ? "#FFFBEB" : "white",
                              color: "var(--text-primary)",
                              fontFamily: "JetBrains Mono, monospace",
                              maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }} title={isModified ? `Was: ${orig}` : String(final ?? "")}>
                              {final ?? ""}
                              {isModified && (
                                <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", display: "block", textDecoration: "line-through", opacity: 0.6 }}>
                                  {orig}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {/* Row expand modal */}
          {expandedRow && (
            <div style={{ position: "fixed", inset: 0, zIndex: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)" }} onClick={() => setExpandedRow(null)} />
              <div data-testid="preview-expand-modal" style={{ position: "relative", background: "white", borderRadius: "0.75rem", padding: "1.25rem", maxWidth: "640px", width: "calc(100% - 2rem)", maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)", zIndex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "0.95rem", margin: 0 }}>Row Detail</h3>
                  <button onClick={() => setExpandedRow(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={16} /></button>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
                  <tbody>
                    {result.final_columns.map(col => {
                      const orig = expandedRow.original?.[col];
                      const fin = expandedRow.final?.[col];
                      const isNew = result.added_columns.includes(col);
                      const isModified = !isNew && orig !== undefined && orig !== fin;
                      return (
                        <tr key={col} style={{ borderBottom: "1px solid var(--border-default)" }}>
                          <td style={{ padding: "0.35rem 0.5rem", fontWeight: 600, fontSize: "0.75rem", color: "var(--text-secondary)", width: "35%", background: isNew ? "#F0FDF4" : isModified ? "#FFFBEB" : "var(--bg-subtle)", whiteSpace: "nowrap" }}>
                            {col}
                            {isNew && <span style={{ marginLeft: 4, fontSize: "0.6rem", background: "#065F46", color: "white", padding: "0.05rem 0.3rem", borderRadius: "9999px" }}>NEW</span>}
                          </td>
                          <td style={{ padding: "0.35rem 0.5rem", fontFamily: "JetBrains Mono, monospace", wordBreak: "break-all" }}>
                            {String(fin ?? "")}
                            {isModified && (
                              <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", textDecoration: "line-through", marginTop: "0.1rem" }}>{String(orig ?? "")}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <button onClick={() => setExpandedRow(null)} className="btn-secondary" style={{ marginTop: "0.75rem", fontSize: "0.82rem" }}>Close</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TransformSetBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [deptId, setDeptId] = useState("");
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [departments, setDepartments] = useState([]);
  const [lookupTables, setLookupTables] = useState([]);
  const [cleanupTables, setCleanupTables] = useState([]);
  const [regexPatterns, setRegexPatterns] = useState([]);
  const [previewCols, setPreviewCols] = useState([]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef(null);
  const { user } = useAuth();

  // Close add menu on outside click
  useEffect(() => {
    const handler = (e) => { if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setShowAddMenu(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [lt, ct, rp] = await Promise.all([
          api.get("/lookups/").then(r => r.data).catch(() => []),
          api.get("/cleanups/").then(r => r.data).catch(() => []),
          api.get("/regex/").then(r => r.data).catch(() => []),
        ]);
        setLookupTables(lt);
        setCleanupTables(ct);
        setRegexPatterns(rp);
      } catch (_) {}
      if (user?.role === "superadmin") {
        api.get("/admin/departments").then(r => setDepartments(r.data)).catch(() => {});
      }
      if (!isNew) {
        try {
          const r = await api.get(`/transform-sets/${id}`);
          setName(r.data.name);
          setDescription(r.data.description || "");
          setDeptId(r.data.department_id || "");
          setSteps(r.data.steps || []);
        } catch (e) {
          setError("Failed to load transform set");
        }
      }
    };
    load();
  }, [id, isNew, user?.role]);

  const addStep = (type) => {
    setSteps(prev => [...prev, makeStep(type)]);
    setShowAddMenu(false);
  };

  const updateStep = (i, updated) => setSteps(prev => prev.map((s, idx) => idx === i ? updated : s));
  const deleteStep = (i) => setSteps(prev => prev.filter((_, idx) => idx !== i));
  const moveStep = (i, dir) => {
    const arr = [...steps];
    const t = i + dir;
    if (t < 0 || t >= arr.length) return;
    [arr[i], arr[t]] = [arr[t], arr[i]];
    setSteps(arr);
  };

  const handleSave = async () => {
    setError("");
    if (!name.trim()) { setError("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { name, description, steps, department_id: deptId || null };
      if (isNew) {
        await api.post("/transform-sets/", payload);
      } else {
        await api.put(`/transform-sets/${id}`, payload);
      }
      navigate("/transform-sets");
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Columns: prefer preview cols; fallback to empty
  const availableCols = previewCols;

  return (
    <Layout>
      <div className="animate-in" style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 7.5rem)" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem", flexShrink: 0 }}>
          <button onClick={() => navigate("/transform-sets")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex" }}>
            <ArrowLeft size={20} strokeWidth={1.5} />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.25rem", color: "var(--text-primary)", margin: 0 }}>
              {isNew ? "New Transform Set" : "Edit Transform Set"}
            </h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", margin: 0 }}>
              Steps are applied in order to raw file data before reconciliation matching
            </p>
          </div>
          <button data-testid="save-ts-btn" className="btn-primary" onClick={handleSave} disabled={saving}
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>

        {error && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.5rem 0.875rem", marginBottom: "0.75rem", fontSize: "0.875rem", color: "#991B1B", display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* Meta fields */}
        <div className="recon-card" style={{ padding: "0.875rem 1rem", marginBottom: "0.75rem", flexShrink: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: user?.role === "superadmin" ? "1.5fr 2fr 1fr" : "1.5fr 2fr", gap: "0.75rem" }}>
            <div>
              <Label>Name *</Label>
              <SI data-testid="ts-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bank Statement Transforms" />
            </div>
            <div>
              <Label>Description</Label>
              <SI value={description} onChange={e => setDescription(e.target.value)} placeholder="What does this transform set do?" />
            </div>
            {user?.role === "superadmin" && (
              <div>
                <Label>Department</Label>
                <SS value={deptId} onChange={e => setDeptId(e.target.value)}>
                  <option value="">— All —</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </SS>
              </div>
            )}
          </div>
        </div>

        {/* Two-column split */}
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: "0.75rem", overflow: "hidden" }}>
          {/* LEFT: Steps panel */}
          <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem", flexShrink: 0 }}>
              <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "0.9rem", color: "var(--text-primary)", margin: 0 }}>
                Steps <span style={{ color: "var(--text-muted)", fontWeight: 400, fontSize: "0.78rem" }}>— applied in order</span>
              </h3>
              <div ref={addMenuRef} style={{ position: "relative" }}>
                <button data-testid="add-step-btn" className="btn-secondary" onClick={() => setShowAddMenu(!showAddMenu)}
                  style={{ display: "flex", alignItems: "center", gap: "0.35rem", fontSize: "0.78rem", padding: "0.3rem 0.65rem" }}>
                  <Plus size={13} /> Add Step <ChevronDown size={11} />
                </button>
                {showAddMenu && (
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 20, background: "white", borderRadius: "0.5rem", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", border: "1px solid var(--border-default)", minWidth: "240px", maxWidth: "320px", maxHeight: "min(420px, calc(100vh - 160px))", overflowY: "auto", overflowX: "hidden" }}>
                    {STEP_TYPES.map(t => (
                      <button key={t.value} onClick={() => addStep(t.value)}
                        style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", padding: "0.5rem 0.875rem", width: "100%", background: "none", border: "none", cursor: "pointer", textAlign: "left" }}
                        onMouseEnter={e => e.currentTarget.style.background = "var(--bg-subtle)"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}>
                        <span style={{ fontSize: "0.65rem", padding: "0.1rem 0.4rem", borderRadius: "9999px", background: `${t.color}18`, color: t.color, border: `1px solid ${t.color}40`, fontWeight: 600, flexShrink: 0, marginTop: 2 }}>
                          {t.label}
                        </span>
                        <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{t.desc}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", paddingRight: "0.25rem" }}>
              {steps.length === 0 ? (
                <div className="recon-card" style={{ padding: "2rem", textAlign: "center" }}>
                  <p style={{ color: "var(--text-muted)", fontSize: "0.8rem", margin: 0 }}>No steps yet. Click "Add Step" to begin.</p>
                </div>
              ) : (
                steps.map((step, i) => (
                  <StepEditor
                    key={step.id}
                    step={step}
                    index={i}
                    total={steps.length}
                    cols={availableCols}
                    lookupTables={lookupTables}
                    cleanupTables={cleanupTables}
                    regexPatterns={regexPatterns}
                    onChange={(updated) => updateStep(i, updated)}
                    onDelete={deleteStep}
                    onMove={moveStep}
                  />
                ))
              )}
            </div>
          </div>

          {/* RIGHT: Preview panel */}
          <div className="recon-card" style={{ padding: "1rem", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "0.9rem", color: "var(--text-primary)", margin: "0 0 0.625rem", flexShrink: 0 }}>
              Live Preview
            </h3>
            <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              <PreviewPanel steps={steps} onColumnsLoaded={setPreviewCols} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
